/**
 * 매출 분석 (Phase 6-5) — mockup analysis 페이지 정합.
 *
 * 5탭 (2026-07-19 주석 갱신):
 *   1. 월별매출분석     — KPI 카드 + 차트 + 표 (①손익/①-1기타비용[접힘]/②국세청/③회사월별/④운영[접힘])
 *                          연도별 3년 비교는 [연도별 매출분석] 탭으로 일원화 (v1.7.7)
 *   2. 연도별 매출분석   — 최근 5개년 (KPI + 표·차트 3세트)
 *   3. 엔드유저별 매출액 — TOP 30 + 직전 2년 미니 표(TOP 10)
 *   4. 거래처별 매출액   — TOP 30 + 기준 토글(제안업체/발행처)
 *   5. 엔드유저별 스토리지 구축용량 — TOP 30 (TB) + 연도 선택
 *
 * 컨트롤바: 분석 연도 / 법인 SM·EJ / 담당자 (끝에 🔔 비상벨 = 비상벨 전용 분석 모드)
 * 헤더 액션: 📥 엑셀 (4시트) / 📄 월간보고서 (영업팀 월간보고서 — 미리보기+특이사항 후 PDF·결재 상신).
 *
 * 신규 매출 등록은 본 화면에서 제거됨 (2026-05-17, "분석은 100% 연동 데이터 조회 전용").
 * 등록 경로: 유지보수 메뉴 / 비상벨 메뉴 헤더의 [+ 등록] 버튼에서 (v1.7.5 독립 메뉴 승격).
 *
 * 권한: viewer 는 메뉴 자체 차단(hideForViewer + ViewerBlocked, 2026-07 정책).
 *   내부의 isViewer 마스킹 분기는 이중 방어 잔재 (현재 도달 불가).
 */

import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import * as analysisApi from '../lib/analysis-api';
import * as salesReportApi from '../lib/sales-report-api';
import { useAuth } from '../lib/auth-context';
import { LastUpdateBadge } from '../components/LastUpdateBadge';
import { VatNote } from '../components/VatNote';
import { PageTabs } from '../components/ui/PageTabs';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import { LoadingLines } from '../components/ui/Skeleton';

// 탭 컴포넌트는 lazy — 활성 탭만 로드. 초기 진입 시 두 컴포넌트 동시 다운로드 회피.
const MonthlyTab = lazy(() =>
  import('../components/AnalysisMonthlyTab').then((m) => ({ default: m.MonthlyTab })),
);
const YearlyTab = lazy(() =>
  import('../components/AnalysisYearlyTab').then((m) => ({ default: m.AnalysisYearlyTab })),
);
const RankingTab = lazy(() =>
  import('../components/AnalysisRankingTab').then((m) => ({ default: m.RankingTab })),
);
// 결제관리 — 2026-08-24 월별매출분석 ⑤ 「수금」에서 독립 (사용자 요청)
const CollectionTab = lazy(() =>
  import('../components/AnalysisCollectionTab').then((m) => ({ default: m.AnalysisCollectionTab })),
);

// 매출 분석은 100% 연동 데이터 조회 전용 — 헤더에서 신규 등록은 제거됨 (2026-05-17).
// 신규 등록은 영업진행 메뉴 → 유지보수 탭 / 비상벨 탭 헤더의 [+ 등록] 버튼에서.

type AnalysisTab = 'monthly' | 'collection' | 'yearly' | 'end-user' | 'invoice' | 'storage';

export function AnalysisPage() {
  const [tab, setTab] = useState<AnalysisTab>('monthly');
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const currentYear = new Date().getFullYear();
  const { user } = useAuth();
  const canExport = user?.role === 'sales' || user?.role === 'mgmt' || user?.role === 'admin';
  const canUseAdvanced = user?.role === 'sales' || user?.role === 'mgmt' || user?.role === 'admin';
  const [excelDownloading, setExcelDownloading] = useState(false);
  const isMonthly = tab === 'monthly';
  const isCollection = tab === 'collection';

  // 📄 영업팀 월간보고서 — 기본값 = 지난달. 흐름: 열면 미리보기 → 특이사항 작성 → PDF 생성
  const now = new Date();
  const lastMonth = now.getMonth() === 0
    ? { y: now.getFullYear() - 1, m: 12 }
    : { y: now.getFullYear(), m: now.getMonth() };
  const [reportOpen, setReportOpen] = useState(false);
  const [reportY, setReportY] = useState(lastMonth.y);
  const [reportM, setReportM] = useState(lastMonth.m);
  const [reportBusy, setReportBusy] = useState(false);
  const [reportNotes, setReportNotes] = useState('');
  const [reportAuthor, setReportAuthor] = useState<analysisApi.MonthlyReportAuthor>(() => {
    try {
      const saved = localStorage.getItem('monthly-report-author');
      if (saved) return JSON.parse(saved) as analysisApi.MonthlyReportAuthor;
    } catch {
      /* 손상된 저장값 무시 */
    }
    return { dept: '', rank: '', name: '' };
  });
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // 📤 결재 상신 (v2.115.0) — 한 달에 문서 하나. 「상신 가능한가」는 화면이 짐작하지 않고 서버 판정을 그대로 쓴다.
  // 상신이 막혀도 다운로드는 막지 않는다 — 이미 올린 달을 다시 내려받는 건 정상 사용.
  const [submitCheck, setSubmitCheck] = useState<salesReportApi.SalesReportCheck | null>(null);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitDone, setSubmitDone] = useState<{ docNo: string } | null>(null);

  function openReport() {
    // 성명 기본값 = 로그인 사용자 (한 번 저장하면 localStorage 값 우선)
    setReportAuthor((a) => (a.name ? a : { ...a, name: user?.name ?? '' }));
    setReportOpen(true);
  }

  /**
   * `/analysis?report=YYYY-MM` (v2.280.0) — 사내결재 문서함 [부서별 월간보고서] 탭의 [📝 보고서 작성]이 영업팀 사람을
   *   여기로 보낸다(기존 작성 방식 그대로 · 그 달로). 한 번 열면 주소에서 지운다 — 새로고침·뒤로가기에 다시 안 열리게.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  /**
   * `/analysis?overdue=long` (v2.327.0 · 개발요청 #39) — 포털챗봇 장기미수 알림의 주소. 결제관리 탭으로 가서 🚨 장기미수 창을 연다.
   *   한 번 열면 주소에서 지운다(새로고침에 다시 안 열리게) · 서버 lib/collection/long-overdue.ts LONG_OVERDUE_PATH 와 짝.
   */
  const [longOverdueOpen, setLongOverdueOpen] = useState(false);
  const consumeLongOverdue = useCallback(() => setLongOverdueOpen(false), []);
  useEffect(() => {
    if (searchParams.get('overdue') !== 'long') return;
    setTab('collection');
    setLongOverdueOpen(true);
    const sp = new URLSearchParams(searchParams);
    sp.delete('overdue');
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  useEffect(() => {
    const rp = searchParams.get('report');
    if (rp === null) return;
    const m = /^(\d{4})-(\d{2})$/.exec(rp);
    if (m && canUseAdvanced) {
      setReportY(Number(m[1]));
      setReportM(Number(m[2]));
      openReport();
    }
    const sp = new URLSearchParams(searchParams);
    sp.delete('report');
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  async function refreshPreview(notes: string, author: analysisApi.MonthlyReportAuthor) {
    setPreviewBusy(true);
    setPreviewError(null);
    try {
      const html = await analysisApi.previewMonthlyReport(reportY, reportM, notes, author);
      setPreviewHtml(html);
    } catch (e) {
      setPreviewError((e as Error).message);
    } finally {
      setPreviewBusy(false);
    }
  }

  async function refreshSubmitCheck() {
    try {
      setSubmitCheck(await salesReportApi.checkSalesReport(reportY, reportM));
    } catch (e) {
      // 조회가 실패하면 「가능하다」고 넘겨짚지 않는다 — null 이면 상신 버튼은 잠긴 채로 둔다
      setSubmitCheck(null);
      setSubmitError((e as Error).message);
    }
  }

  // 모달 열림·연/월 변경 시 미리보기 자동 갱신 (특이사항·작성자는 [미리보기 갱신] 버튼으로 반영)
  // 상신 가능 여부도 같은 시점에 다시 묻는다 — 달을 바꾸면 지난 달의 판정이 남아 있으면 안 된다.
  useEffect(() => {
    if (!reportOpen) return;
    setPreviewHtml(null);
    void refreshPreview(reportNotes, reportAuthor);
    setSubmitCheck(null);
    setSubmitError(null);
    setSubmitDone(null);
    void refreshSubmitCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportOpen, reportY, reportM]);

  /** variant: 'print' = A4 인쇄용 · 'slides' = 회의실 화면용 16:9 발표본 (같은 내용·다른 배치) */
  async function handleReport(variant: 'print' | 'slides') {
    setReportBusy(true);
    try {
      localStorage.setItem('monthly-report-author', JSON.stringify(reportAuthor));
      await analysisApi.downloadMonthlyReport(reportY, reportM, reportNotes, reportAuthor, variant);
      setReportOpen(false);
    } catch (e) {
      alert(`월간보고서 생성 실패: ${(e as Error).message}`);
    } finally {
      setReportBusy(false);
    }
  }

  /**
   * 결재 상신 — 숫자는 서버가 이 시점에 집계해 문서에 붙박는다(이후 데이터가 바뀌어도 그대로).
   * 되돌릴 수 없는 동작이라 확인 창을 한 번 받고, 성공하면 판정을 다시 물어 버튼을 잠근다.
   */
  async function handleSubmitApproval() {
    const ok = window.confirm(
      `${reportY}년 ${reportM}월 영업팀 월간보고서를 결재 상신합니다.\n` +
        `결재선: 담당(작성자) → 부서장 → 대표이사\n\n` +
        `보고서의 숫자는 지금 이 시점의 집계로 확정되며, 이후 데이터가 바뀌어도 문서에는 반영되지 않습니다.\n` +
        `상신할까요?`,
    );
    if (!ok) return;
    setSubmitBusy(true);
    setSubmitError(null);
    try {
      const r = await salesReportApi.createSalesReport({
        year: reportY,
        month: reportM,
        notes: reportNotes,
        submit: true,
      });
      setSubmitDone({ docNo: r.docNo });
      await refreshSubmitCheck();
    } catch (e) {
      // 서버 오류 문구는 이미 사용자용 한글 — 가공하지 않고 그대로 보여준다
      setSubmitError((e as Error).message);
    } finally {
      setSubmitBusy(false);
    }
  }

  // 법인·담당자 필터 (월별매출분석 탭 전용). 빈 문자열 = 전체.
  const [company, setCompany] = useState<'' | 'SM' | 'EJ'>('');
  const [emp, setEmp] = useState<string>('');
  const filterOptions = useQuery({
    queryKey: ['analysis', 'filter-options'],
    queryFn: analysisApi.getFilterOptions,
    staleTime: 5 * 60_000,
  });

  async function handleExcel() {
    setExcelDownloading(true);
    try {
      await analysisApi.downloadAnalysisExcel(year, { company, emp });
    } catch (e) {
      alert(`엑셀 다운로드 실패: ${(e as Error).message}`);
    } finally {
      setExcelDownloading(false);
    }
  }

  return (
    <div className="p-8 analysis-no-sticky">
      {/* 헤더 */}
      <div className="flex items-end justify-between mb-5">
        <div>
          <h1 className="ui-page-title">매출 분석</h1>
          <p className="ui-page-sub flex items-center gap-2 flex-wrap">
            <span>연도 단위 — 영업·유지보수·비상벨 매출 통합 분석</span>
            <VatNote />
          </p>
          <LastUpdateBadge className="mt-1" />
        </div>
        <div className="flex gap-2">
          {canExport && (
            <button
              type="button"
              onClick={handleExcel}
              disabled={excelDownloading}
              className={`btn ${excelDownloading ? 'btn-secondary' : 'btn-secondary'}`}
            >
              {excelDownloading ? '다운로드 중…' : '📥 엑셀'}
            </button>
          )}
          {canUseAdvanced && (
            <button
              type="button"
              onClick={openReport}
              className="btn btn-outline"
              title="영업팀 월간보고서 PDF — 미리보기 확인 후 기타 특이사항을 적고 생성"
            >
              📄 월간보고서
            </button>
          )}
        </div>
      </div>

      {/* 탭 6개 — 전환 탭을 맨 위로 (탭 선택 후 그 아래 필터) */}
      <PageTabs
        tabs={[
          { key: 'monthly', label: '월별매출분석' },
          { key: 'collection', label: '💵 결제관리' },
          { key: 'yearly', label: '연도별 매출분석' },
          { key: 'end-user', label: '엔드유저별 매출액' },
          { key: 'invoice', label: '거래처별 매출액' },
          { key: 'storage', label: '엔드유저별 스토리지 구축용량' },
        ]}
        value={tab}
        onChange={setTab}
        ariaLabel="매출 분석 보기"
      />

      {/* 컨트롤 바 — 월별·연도별 매출분석 탭에서 표시. 탭 아래에 위치.
          월별만 분석 연도 선택. 연도별은 최근 5개년 고정 (연도 칸 없음).
          그 외 ranking 탭들은 항상 최근 3년 자료 분석이라 필터 무관. */}
      {(isMonthly || isCollection || tab === 'yearly') && (
        <div className="bg-white rounded shadow-sm border border-slate-200 p-4 mb-4">
          <div className={`grid gap-3 items-end text-sm ${isMonthly || isCollection ? 'grid-cols-4' : 'grid-cols-3'}`}>
            {(isMonthly || isCollection) && (
              <div>
                <label className="ui-label">분석 연도</label>
                <select
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  className="ui-input w-full mt-1 font-bold"
                >
                  {[year + 1, year, year - 1, year - 2, year - 3]
                    .filter((y, i, arr) => arr.indexOf(y) === i)
                    .map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                </select>
              </div>
            )}
            <div>
              <label className="ui-label">법인</label>
              <select
                value={company}
                onChange={(e) => setCompany(e.target.value as '' | 'SM' | 'EJ')}
                className="ui-input w-full mt-1"
              >
                <option value="">전체</option>
                {(filterOptions.data?.companies ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="ui-label">담당자</label>
              <select
                value={emp}
                onChange={(e) => setEmp(e.target.value)}
                className="ui-input w-full mt-1"
              >
                <option value="">전체</option>
                {(filterOptions.data?.reps ?? []).map((r) => (
                  <option key={r.empId} value={r.empId}>
                    {r.name}
                  </option>
                ))}
                {/* 비상벨 = 시스템계정 BELL. 선택 시 비상벨만 (영업·유지보수 0) */}
                <option value="BELL">🔔 비상벨</option>
              </select>
            </div>
            <div className="text-xs text-slate-500 leading-snug">
              {isCollection ? (
                <>
                  실제 통장에 오가는 돈 기준 — 전 칸 <strong>부가세 포함</strong>.
                  {(company || emp) && (
                    <span className="text-blue-600"> 필터 적용 중 — 선택한 법인·담당자 몫만 셉니다.</span>
                  )}
                </>
              ) : isMonthly ? (
                <>
                  매출은 계산서 발행월 기준 — 영업건·유지보수·비상벨 합산.
                  {(company || emp) && (
                    <span className="text-blue-600">
                      {' '}필터 적용 중 — 국세청 손익(②)·당월 발행 매입은 전체 기준이라 숨겨집니다.
                    </span>
                  )}
                </>
              ) : (
                <>
                  최근 5개년 ({currentYear - 4} ~ {currentYear}) 매출·손익 (별도 연도 필터 없음).
                  {(company || emp) && (
                    <span className="text-blue-600">
                      {' '}필터 적용 중 — 국세청 손익(②)·회사별(③)은 전체 기준이라 매출총이익(①)만 표시됩니다.
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {(tab === 'end-user' || tab === 'invoice') && (
        <div className="mb-4 text-xs text-slate-500">
          ※ 본 탭은 최근 3년 자료 ({currentYear - 2} ~ {currentYear}) 를 기준으로 분석합니다. 별도 연도 필터 없음.
          매출은 <strong>계산서 발행일</strong> 기준 (영업·유지보수·비상벨 동일 — 2026-07 통일).
        </div>
      )}

      {/* 탭 컴포넌트 lazy — 활성 탭만 로드.
          ranking 탭들 (end-user/invoice/storage) 은 사용자 year 와 무관하게
          항상 currentYear 기준 최근 3년 자료 분석 (사용자 정책). */}
      <Suspense fallback={
        <div className="bg-white rounded border border-slate-200 p-12 text-center text-slate-400 text-sm"><LoadingLines /></div>
      }>
        {tab === 'monthly' && (
          <MonthlyTabContainer year={year} company={company} emp={emp} />
        )}
        {tab === 'collection' && (
          <CollectionTab
            year={year}
            company={company === '' ? null : company}
            emp={emp === '' ? null : emp}
            filterActive={company !== '' || emp !== ''}
            initialLongOverdue={longOverdueOpen}
            onLongOverdueConsumed={consumeLongOverdue}
          />
        )}
        {tab === 'yearly' && <YearlyTabContainer year={currentYear} company={company} emp={emp} />}
        {tab === 'end-user' && <EndUserTabContainer year={currentYear} />}
        {tab === 'invoice' && <InvoiceTabContainer year={currentYear} />}
        {tab === 'storage' && <StorageTabContainer year={currentYear} />}
      </Suspense>

      {/* 📄 영업팀 월간보고서 모달 — 미리보기 → 기타 특이사항 작성 → PDF 생성 */}
      {reportOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
        >
          <div
            className="bg-white rounded shadow-xl border border-slate-200 p-5 w-[1060px] max-w-[96vw] max-h-[94vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="ui-subsection-title">📄 영업팀 월간보고서</h3>
                <p className="text-xs text-slate-500 leading-snug">
                  포털 데이터로 자동 작성된 미리보기를 확인하고, 기타 특이사항을 적은 뒤 PDF를 생성하세요 (전사 기준).<br />
                  <b>🖨 인쇄용</b> = A4 결재·보관본 · <b>📽 발표용</b> = 회의실 화면에 띄우는 16:9 슬라이드
                  <span className="text-slate-400"> (내용은 같습니다)</span><br />
                  <span className="text-slate-400">
                    숫자는 시스템이 <b>전사 기준</b>으로 집계합니다 — 화면 위 법인·담당자 필터와 무관하며, 사람이 적는 것은 [기타 특이사항] 뿐입니다.
                  </span>
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <select
                  value={reportY}
                  onChange={(e) => setReportY(Number(e.target.value))}
                  className="ui-input"
                >
                  {[0, 1, 2, 3].map((d) => (
                    <option key={d} value={currentYear - d}>{currentYear - d}년</option>
                  ))}
                </select>
                <select
                  value={reportM}
                  onChange={(e) => setReportM(Number(e.target.value))}
                  className="ui-input"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>{m}월</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-4 flex-1 min-h-0">
              {/* 미리보기 (PDF 와 동일 내용 · 화면 폰트로 표시) */}
              <div className="flex-1 min-w-0 border border-slate-300 rounded bg-slate-50 overflow-hidden relative">
                {previewBusy && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-sm text-slate-500 z-10">
                    미리보기 생성 중…
                  </div>
                )}
                {previewError ? (
                  <div className="p-4 text-sm text-red-600">미리보기 실패: {previewError}</div>
                ) : previewHtml ? (
                  <iframe
                    title="월간보고서 미리보기"
                    srcDoc={previewHtml}
                    sandbox=""
                    className="w-full h-[560px] bg-white"
                  />
                ) : (
                  <div className="p-4 text-sm text-slate-400">미리보기를 불러오는 중…</div>
                )}
              </div>

              {/* 작성자 + 기타 특이사항 */}
              <div className="w-72 shrink-0 flex flex-col gap-3">
                <div>
                  <div className="text-xs font-semibold text-slate-600 mb-1">작성자 정보 (표지에 표시 · 자동 기억)</div>
                  <div className="flex gap-1.5">
                    <input
                      value={reportAuthor.dept}
                      onChange={(e) => setReportAuthor((a) => ({ ...a, dept: e.target.value }))}
                      placeholder="소속"
                      className="ui-input ui-input-sm w-0 flex-1"
                    />
                    <input
                      value={reportAuthor.rank}
                      onChange={(e) => setReportAuthor((a) => ({ ...a, rank: e.target.value }))}
                      placeholder="직급"
                      className="ui-input ui-input-sm w-16"
                    />
                    <input
                      value={reportAuthor.name}
                      onChange={(e) => setReportAuthor((a) => ({ ...a, name: e.target.value }))}
                      placeholder="성명"
                      className="ui-input ui-input-sm w-20"
                    />
                  </div>
                </div>
                <div className="flex-1 flex flex-col min-h-0">
                  <div className="text-xs font-semibold text-slate-600 mb-1">
                    기타 특이사항 <span className="font-normal text-slate-400">— 한 줄 = 항목 하나</span>
                  </div>
                  <textarea
                    value={reportNotes}
                    onChange={(e) => setReportNotes(e.target.value)}
                    placeholder={'예)\n[인포트렌드] 6월 여신 송금 $40,206\n[마케팅] 보안박람회 참가 (3.18~20 킨텍스)\n[인증] KC 인증 완료 제품 반출 진행'}
                    className="ui-input ui-textarea flex-1 min-h-[300px] resize-none"
                  />
                  <p className="text-xs text-slate-500 mt-1 leading-snug">
                    포털에 없는 내용(마케팅·출장·인증·행정 등)을 적으면 보고서의 [기타 특이사항] 칸에 들어갑니다.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void refreshPreview(reportNotes, reportAuthor)}
                  disabled={previewBusy}
                  className="btn btn-secondary"
                >
                  🔄 미리보기 갱신
                </button>
              </div>
            </div>

            {/* 결재 상신 상태 — 상신이 막힌 달이라도 아래 다운로드 버튼은 그대로 쓸 수 있다 */}
            {submitDone ? (
              <div className="mt-3 text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-3 py-2 flex items-center gap-2 flex-wrap">
                <span>
                  결재 상신 완료 — 문서번호 <b>{submitDone.docNo}</b> · 부서장 → 대표이사 순으로 결재됩니다.
                </span>
                <Link to="/expenses?tab=monthly" className="underline font-medium">
                  사내결재 문서함에서 보기
                </Link>
              </div>
            ) : submitError ? (
              <div className="mt-3 text-sm bg-rose-50 border border-rose-200 text-rose-600 rounded px-3 py-2 whitespace-pre-line">
                {submitError}
              </div>
            ) : submitCheck && !submitCheck.canSubmit ? (
              <div className="mt-3 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2 flex items-center gap-2 flex-wrap">
                <span>{submitCheck.reason ?? '지금은 결재 상신할 수 없습니다.'}</span>
                {submitCheck.existing && (
                  <Link to="/expenses?tab=monthly" className="underline font-medium">
                    {submitCheck.existing.docNo} 문서 보기
                  </Link>
                )}
              </div>
            ) : null}

            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setReportOpen(false)}
                disabled={reportBusy}
                className="btn btn-secondary"
              >
                취소
              </button>
              <button
                type="button"
                onClick={() => void handleReport('print')}
                disabled={reportBusy || previewBusy || !previewHtml}
                title="A4 세로 · 결재판 포함 — 결재 올리고 보관하는 용도"
                className="btn btn-primary"
              >
                {reportBusy ? '생성 중…' : '🖨 인쇄용 PDF 다운로드'}
              </button>
              <button
                type="button"
                onClick={() => void handleReport('slides')}
                disabled={reportBusy || previewBusy || !previewHtml}
                title="16:9 슬라이드 · 같은 내용을 발표 형식으로 — 회의실 화면에 띄우는 용도"
                className="btn btn-primary"
              >
                {reportBusy ? '생성 중…' : '📽 발표용 PDF 다운로드'}
              </button>
              <button
                type="button"
                onClick={() => void handleSubmitApproval()}
                disabled={submitBusy || reportBusy || !submitCheck?.canSubmit}
                title={
                  submitCheck
                    ? (submitCheck.canSubmit
                        ? '담당(작성자) → 부서장 → 대표이사 결재선으로 올립니다 · 숫자는 상신 시점으로 확정'
                        : (submitCheck.reason ?? '지금은 결재 상신할 수 없습니다'))
                    : '상신 가능 여부를 확인하는 중입니다'
                }
                className="btn btn-success-solid"
              >
                {submitBusy ? '상신 중…' : '📤 결재 상신'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 탭별 컨테이너 (각각 useQuery) ──
function MonthlyTabContainer({
  year,
  company,
  emp,
}: {
  year: number;
  company: '' | 'SM' | 'EJ';
  emp: string;
}) {
  const query = useQuery({
    queryKey: ['analysis', 'monthly', year, company, emp],
    queryFn: () => analysisApi.getMonthlyAnalysis(year, { company, emp }),
    staleTime: 30_000,
  });
  return (
    <QueryWrap query={query}>{(data) => <MonthlyTab data={data} />}</QueryWrap>
  );
}
function YearlyTabContainer({
  year,
  company,
  emp,
}: {
  year: number;
  company: '' | 'SM' | 'EJ';
  emp: string;
}) {
  const query = useQuery({
    queryKey: ['analysis', 'yearly', year, company, emp],
    queryFn: () => analysisApi.getYearlyAnalysis(year, { company, emp }),
    staleTime: 30_000,
  });
  return (
    <QueryWrap query={query}>{(data) => <YearlyTab data={data} />}</QueryWrap>
  );
}
function EndUserTabContainer({ year }: { year: number }) {
  const query = useQuery({
    queryKey: ['analysis', 'end-user', year],
    queryFn: () => analysisApi.getEndUserRanking(year, 30),
    staleTime: 30_000,
  });
  return (
    <QueryWrap query={query}>
      {(data) => (
        <RankingTab
          data={data}
          title="엔드유저별 매출액"
          keyLabel="엔드유저"
          year={year}
        />
      )}
    </QueryWrap>
  );
}
function InvoiceTabContainer({ year }: { year: number }) {
  // 기준 토글 — 제안업체(실제 고객) vs 계산서 발행처(원청·채권처, 2026-07-11 신설)
  const [basis, setBasis] = useState<'proposal' | 'billing'>('proposal');
  const query = useQuery({
    queryKey: ['analysis', 'invoice', basis, year],
    queryFn: () =>
      basis === 'proposal'
        ? analysisApi.getInvoiceCompanyRanking(year, 30)
        : analysisApi.getBillingCompanyRanking(year, 30),
    staleTime: 30_000,
  });
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-slate-500">기준</span>
        <SegmentedControl
          ariaLabel="거래처별 매출 기준"
          options={[
            { key: 'proposal', label: '제안업체' },
            { key: 'billing', label: '계산서 발행처' },
          ]}
          value={basis}
          onChange={setBasis}
        />
        <span className="text-xs text-slate-500">
          {basis === 'proposal'
            ? '제안업체 = 사업을 함께 만든 실제 고객'
            : '계산서 발행처 = 세금계산서를 발행한 원청 — 매출 의존도(집중도) 관점'}
        </span>
      </div>
      <QueryWrap query={query}>
        {(data) => (
          <RankingTab
            data={data}
            title={
              basis === 'proposal'
                ? '거래처별 매출액 (제안업체 기준)'
                : '거래처별 매출액 (계산서 발행처 기준)'
            }
            keyLabel={basis === 'proposal' ? '제안업체' : '발행처'}
            year={year}
          />
        )}
      </QueryWrap>
    </div>
  );
}
function StorageTabContainer({ year }: { year: number }) {
  // 스토리지 탭은 연도 선택 가능 — 선택 연도 기준 30위 + 직전 2년 비교
  const [selYear, setSelYear] = useState(year);
  const query = useQuery({
    queryKey: ['analysis', 'storage', selYear],
    queryFn: () => analysisApi.getStorageRanking(selYear, 30),
    staleTime: 30_000,
  });
  const yearOpts: number[] = [];
  for (let y = year; y >= year - 5; y--) yearOpts.push(y);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-500">기준 연도</span>
        <select
          value={selYear}
          onChange={(e) => setSelYear(Number(e.target.value))}
          className="ui-input ui-input-sm"
        >
          {yearOpts.map((y) => (
            <option key={y} value={y}>{y}년</option>
          ))}
        </select>
        <span className="text-xs text-slate-500">위 카드 = 연도별 전체 합계(4개년) · 표 = 선택 연도 기준 30위까지 (직전 2년 비교 포함) · 스토리지 출고일 기준 TB</span>
      </div>
      <QueryWrap query={query}>
        {(data) => (
          <>
            <StorageYearTotals totals={data.yearTotals ?? []} thisYear={year} />
            <RankingTab
              data={data}
              title="엔드유저별 스토리지 구축용량"
              keyLabel="엔드유저"
              year={selYear}
            />
          </>
        )}
      </QueryWrap>
    </div>
  );
}

/**
 * 연도별 스토리지 구축용량 합계 4개년 카드 (v2.230.0 · 개발 요청 #12) — TOP 30 표 위.
 *   ⭐그 해 **엔드유저 전부**의 합(서버 yearTotals · 표와 같은 뷰) — 아래 표 30줄을 더한 값과는 다를 수 있다.
 */
function StorageYearTotals({ totals, thisYear }: { totals: { year: number; tb: number; devices: number }[]; thisYear: number }) {
  if (totals.length === 0) return null;
  const fmt = (n: number): string => (Math.round(n * 10) / 10).toLocaleString('ko-KR');
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
      {totals.map((t, i) => {
        const older = totals[i + 1];
        const diff = older && older.tb > 0 ? (t.tb - older.tb) / older.tb : null;
        return (
          <div key={t.year} className={`rounded border px-3 py-2 ${i === 0 ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white'}`}>
            <div className="text-xs text-slate-500">
              {t.year}년 스토리지 구축용량{t.year === thisYear && <span className="ml-1 text-amber-600">(올해 · 오늘까지)</span>}
            </div>
            <div className="text-xl font-bold text-slate-800 num">{fmt(t.tb)} <span className="text-sm font-medium text-slate-500">TB</span></div>
            <div className="text-xs text-slate-500 flex gap-2 flex-wrap">
              <span>HDD {t.devices.toLocaleString('ko-KR')}개</span>
              {diff !== null && (
                <span className={diff >= 0 ? 'text-emerald-700' : 'text-rose-600'}>
                  {older!.year}년 대비 {diff >= 0 ? '+' : ''}{(diff * 100).toFixed(1)}%
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function QueryWrap<T>({
  query,
  children,
}: {
  query: { isLoading: boolean; isError: boolean; error: unknown; data: T | undefined };
  children: (data: T) => React.ReactNode;
}) {
  if (query.isLoading) {
    return (
      <div className="bg-white rounded border border-slate-200 p-12 text-center text-slate-400 text-sm"><LoadingLines /></div>
    );
  }
  if (query.isError) {
    return (
      <div className="bg-white rounded border border-slate-200 p-12 text-center text-red-500 text-sm">
        오류: {(query.error as Error).message}
      </div>
    );
  }
  if (!query.data) return null;
  return <>{children(query.data)}</>;
}
